import React, { useEffect, useState } from 'react';
import { Box, Typography, FormControl, FormGroup, FormControlLabel, Grid, Card, CardContent, FormHelperText, Radio } from '@mui/material';
import { Controller, UseFormReturn } from 'react-hook-form';
import { TicketFormData } from '../types';
import { useTicketTypeConfigSearchQuery } from '../../../../../services/ticketEasyService/ticketEasy-api';
import { TicketTypeConfigSearchRequest } from '../../../../../services/ticketEasyService/commands/TicketTypeConfigSearchRequest';
import { TicketTypeConfig } from '../../../../../services/ticketEasyService/model/ticketEasy/ticketTypeConfig/TicketTypeConfig';

interface TicketTypesStepProps {
  form: UseFormReturn<TicketFormData>;
  selectedModule: string;
}

export const TicketTypesStep: React.FC<TicketTypesStepProps> = ({ 
  form, 
  selectedModule
}) => {
  const { control, setValue, watch, formState: { errors } } = form;
  const [ticketTypes, setTicketTypes] = useState<TicketTypeConfig[]>([]);
  
  // Get available ticket types for the selected module
  const command = new TicketTypeConfigSearchRequest(selectedModule, "");
  const { data: ticketTypeConfigList, isLoading } = useTicketTypeConfigSearchQuery(command, { 
    skip: !selectedModule
  });
  
  // Watch for the current selected ticketType (single value now)
  const selectedTicketType = watch('ticketTypes')?.[0] || '';
  
  // Update available ticket types when the query returns data
  useEffect(() => {
    if (ticketTypeConfigList) {
      setTicketTypes(ticketTypeConfigList);
    }
  }, [ticketTypeConfigList]);
  
  // Initialize the selection if there's only one ticket type
  useEffect(() => {
    if (ticketTypes.length === 1) {
      setValue('ticketTypes', [ticketTypes[0].ticketType]);
    }
  }, [ticketTypes, setValue]);

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, color: '#34495e' }}>
        Select Ticket Type
      </Typography>
      
      {isLoading ? (
        <Typography>Loading ticket types...</Typography>
      ) : ticketTypes.length === 0 ? (
        <Typography color="error">
          No ticket types found for this module. Please contact your administrator.
        </Typography>
      ) : (
        <Controller
          name="ticketTypes"
          control={control}
          defaultValue={[]}
          rules={{ 
            validate: value => 
              (value && value.length > 0) || "Please select a ticket type"
          }}
          render={({ field }) => (
            <FormControl 
              component="fieldset" 
              sx={{ width: '100%' }}
              error={!!errors.ticketTypes}
            >
              <FormGroup>
                <Grid container spacing={2}>
                  {ticketTypes.map((type) => (
                    <Grid item xs={12} sm={6} md={4} key={type.id}>
                      <Card 
                        variant="outlined" 
                        sx={{ 
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          border: selectedTicketType === type.ticketType 
                            ? '2px solid #3f51b5' 
                            : errors.ticketTypes
                              ? '1px solid #d32f2f'
                              : '1px solid #e0e0e0',
                          backgroundColor: selectedTicketType === type.ticketType 
                            ? '#f0f4ff' 
                            : 'white'
                        }}
                        onClick={() => {
                          // Set only the selected ticket type
                          field.onChange([type.ticketType]);
                        }}
                      >
                        <CardContent>
                          <FormControlLabel
                            control={
                              <Radio 
                                checked={selectedTicketType === type.ticketType}
                                onChange={() => {
                                  // Set only the selected ticket type
                                  field.onChange([type.ticketType]);
                                }}
                              />
                            }
                            label={
                              <Box>
                                <Typography variant="subtitle1">{type.ticketType}</Typography>
                                <Typography variant="body2" color="textSecondary">
                                  {type.description}
                                </Typography>
                              </Box>
                            }
                          />
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </FormGroup>
              {errors.ticketTypes && (
                <FormHelperText error>
                  {errors.ticketTypes.message?.toString()}
                </FormHelperText>
              )}
            </FormControl>
          )}
        />
      )}
    </Box>
  );
};